import { currentQueuedModalAtom } from '@/atoms/modalQueueAtoms';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ScreenName } from '@heidi/analytics/vocabulary';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useOpenRoutinesSurface } from './useOpenRoutinesSurface';

import {
  ROUTINES_SPOTLIGHT_TOTAL_STEPS,
  routinesSpotlightListAnchorsMountedAtom,
  routinesSpotlightNeedsSidebarOpenAtom,
  routinesSpotlightSidebarAnchorsMountedAtom,
  routinesSpotlightSidebarReadyAtom,
  routinesSpotlightStepAtom,
} from '@heidi/agents/data';

/**
 * Effect-free actions for the tour, safe to call from every step component
 * (multiple callers share the same atom state). Keep the lifecycle effects out
 * of here — those live in `useRoutinesSpotlightTour`, mounted exactly once.
 */
export function useRoutinesSpotlightActions() {
  const step = useAtomValue(routinesSpotlightStepAtom);
  const setStep = useSetAtom(routinesSpotlightStepAtom);
  const openRoutinesSurface = useOpenRoutinesSurface();
  const { handleClose } = useQueuedModal(
    ModalId.T4_CUSTOM_AUDIENCE_ROUTINES_ONBOARDING_SPOTLIGHT
  );

  /** Advance to the next step; on the last step this is a no-op (use `close`). */
  const next = useCallback(() => {
    if (step === 1) {
      // Same handoff as the sidebar Routines entry — navigate to Work home
      // browse so steps 2–5 have anchors (not just stamp the view atom).
      openRoutinesSurface(null);
    }
    setStep((current) => {
      if (current === null) return current;
      return current < ROUTINES_SPOTLIGHT_TOTAL_STEPS
        ? ((current + 1) as typeof current)
        : current;
    });
  }, [openRoutinesSurface, setStep, step]);

  /**
   * Close the tour: clear step state, return the queue slot and mark the modal
   * seen so it never shows again. `handleClose` handles the seen-state update +
   * dequeue.
   */
  const close = useCallback(() => {
    setStep(null);
    handleClose();
  }, [handleClose, setStep]);

  return { step, next, close };
}

/** Delay before starting the tour so Work chrome can render and the user can orient. */
const START_DELAY_MS = 800;

/**
 * Step 1 Next navigates to the Routines browse surface. Give the list a beat
 * to mount before treating missing step 2–5 anchors as "user left".
 */
const LIST_ANCHOR_GRACE_MS = 1500;

/**
 * Auto-starts the tour at step 1 once T4_CUSTOM_AUDIENCE_ROUTINES_ONBOARDING_SPOTLIGHT
 * reaches the front of the modal queue, and fires the one-time "shown" screen
 * view. Mount once in a long-lived layout (GlobalModalsWrapper). Mirrors
 * NEW_SESSION_UPLOAD_SPOTLIGHT.
 */
export function useRoutinesSpotlightTour() {
  const currentModal = useAtomValue(currentQueuedModalAtom);
  const isQueueActive =
    currentModal?.id === ModalId.T4_CUSTOM_AUDIENCE_ROUTINES_ONBOARDING_SPOTLIGHT;

  const step = useAtomValue(routinesSpotlightStepAtom);
  const setStep = useSetAtom(routinesSpotlightStepAtom);
  const sidebarAnchorsMounted = useAtomValue(routinesSpotlightSidebarAnchorsMountedAtom);
  const listAnchorsMounted = useAtomValue(routinesSpotlightListAnchorsMountedAtom);
  const isSidebarReady = useAtomValue(routinesSpotlightSidebarReadyAtom);
  const setNeedsSidebarOpen = useSetAtom(routinesSpotlightNeedsSidebarOpenAtom);
  const { handleClose } = useQueuedModal(
    ModalId.T4_CUSTOM_AUDIENCE_ROUTINES_ONBOARDING_SPOTLIGHT
  );

  const hasShownRef = useRef(false);
  const isTourActive = step !== null;

  // Step 1 anchors the sidebar Routines row. Ask ChatSidebar (via
  // RoutinesSidebarNavEntry) to open when the tour is queued / on step 1.
  // Do not import chatAtoms — Chat quarantine is shrink-only.
  useEffect(() => {
    const shouldNeedOpen = isQueueActive || step === 1;
    setNeedsSidebarOpen(shouldNeedOpen);
    return () => {
      if (shouldNeedOpen) setNeedsSidebarOpen(false);
    };
  }, [isQueueActive, step, setNeedsSidebarOpen]);

  useEffect(() => {
    if (!isQueueActive && isTourActive) {
      hasShownRef.current = false;
      setStep(null);
    }
  }, [isQueueActive, isTourActive, setStep]);

  // Close if the sidebar Routines entry unmounts (left Work / flag off).
  useEffect(() => {
    if (!isTourActive) return;
    if (!sidebarAnchorsMounted) {
      setStep(null);
      handleClose();
    }
  }, [isTourActive, sidebarAnchorsMounted, setStep, handleClose]);

  // Close if browse-list anchors unmount on steps 2–5 (left Routines for Work
  // home, or the panel left the tree). Grace period covers step-1 Next
  // navigation before WorkflowsListPanel mounts.
  useEffect(() => {
    if (!isTourActive) return;
    if (step === null || step < 2) return;
    if (listAnchorsMounted) return;

    const timeoutId = window.setTimeout(() => {
      setStep(null);
      handleClose();
    }, LIST_ANCHOR_GRACE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isTourActive, listAnchorsMounted, step, setStep, handleClose]);

  useEffect(() => {
    if (hasShownRef.current) return;
    if (!isQueueActive) return;
    if (isTourActive) return;
    if (!isSidebarReady) return;

    const timeoutId = window.setTimeout(() => {
      hasShownRef.current = true;
      setStep(1);
      track({
        event_name: TrackingEventName.ScreenViewed,
        props: { screen_name: ScreenName.RoutinesSpotlight },
      });
    }, START_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isQueueActive, isTourActive, isSidebarReady, setStep]);

  return { isQueueActive, isTourActive };
}
